import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { Root as MdastRoot, TopLevelContent } from 'mdast';
import { toMarkdown } from 'mdast-util-to-markdown';
import { gfmTableToMarkdown } from 'mdast-util-gfm-table';
import { gfmTaskListItemToMarkdown } from 'mdast-util-gfm-task-list-item';
import { gfmStrikethroughToMarkdown } from 'mdast-util-gfm-strikethrough';
import { fromPMHandlers as defaultHandlers } from './handlers/index';
import type { FromPMHandlers, FromProseMirrorResult } from './types';
import { extractAnnotationRanges } from './extractAnnotationRanges';

/**
 * Post-process markdown output to undo the `&#x20;` and `&#x9;` encoding
 * that `mdast-util-to-markdown` applies to leading/trailing whitespace in
 * phrasing contexts. See EDITOR-6558 comment in `fromProseMirror`.
 *
 * The library's `unsafe` configuration only supports *adding* patterns,
 * not removing defaults, so we can't prevent the encoding. Instead we
 * replace the character references back to literal characters after
 * serialisation.
 */
function undoEdgeWhitespaceEncoding(md: string): string {
	return md.replace(/&#x20;/gu, ' ').replace(/&#x9;/gu, '\t');
}

/**
 * Convert a ProseMirror document node (or its JSON representation) to a
 * markdown string plus a separate array of annotation ranges.
 *
 * The returned `markdown` is a clean string with no annotation syntax embedded.
 * The `annotations` array contains one entry per annotation mark found in the
 * source document, with character offsets into `markdown` so they can be used
 * directly as CM6 DecorationSet ranges or passed to `applyAnnotationRanges`
 * to reconstruct the PM document on commit.
 *
 * Use `fromProseMirror` for the simpler string-only version (backward
 * compatible). Use this function when you need annotation range data.
 */
export function fromProseMirrorWithAnnotations(
	schema: Schema,
	handlers: FromPMHandlers = defaultHandlers,
) {
	return function (doc: PMNode | Record<string, unknown>): FromProseMirrorResult {
		let pmDoc: PMNode;
		if ('type' in doc && typeof doc.type === 'string') {
			// It's a JSON representation — hydrate it
			pmDoc = schema.nodeFromJSON(doc);
		} else {
			pmDoc = doc as PMNode;
		}

		const children: TopLevelContent[] = [];

		pmDoc.forEach((child) => {
			const handler = handlers[child.type.name];
			if (!handler) {
				// Throw on unsupported top-level nodes. Silently dropping at the top level
				// would mean a document with only unknown blocks would serialize to an empty
				// string with no indication something went wrong — a data-loss footgun.
				// Nested unknown nodes are still silently skipped by `convertBlockChildren`
				// so partial-but-meaningful output is still possible for documents whose
				// structure is mostly known.
				throw new Error(
					`ProseMirror node type "${child.type.name}" is not supported by mdast-util-from-pm. ` +
						`Either add a handler for this node type, or filter it out of the document before serializing.`,
				);
			}
			const result = handler(child);
			if (result) {
				if (Array.isArray(result)) {
					children.push(...(result as TopLevelContent[]));
				} else {
					children.push(result as TopLevelContent);
				}
			}
		});

		const mdast: MdastRoot = { type: 'root', children };

		// EDITOR-6558: `mdast-util-to-markdown` encodes leading/trailing
		// whitespace in phrasing contexts as HTML character references
		// (e.g. `&#x20;`). This is technically correct per CommonMark but
		// undesirable for our use case — it makes the markdown unreadable
		// and breaks round-trip identity in the source view.
		//
		// The library's `unsafe` configuration only supports *adding*
		// patterns, not removing defaults, so we post-process the output
		// to replace the character references back to literal characters.
		const rawMarkdown = toMarkdown(mdast, {
			bullet: '*',
			listItemIndent: 'one',
			fences: true,
			// Both are functions in the ESM version (v1.x) installed in AFM
			extensions: [gfmTableToMarkdown(), gfmTaskListItemToMarkdown(), gfmStrikethroughToMarkdown()],
		});

		const markdown = undoEdgeWhitespaceEncoding(rawMarkdown);

		// Extract annotation ranges from the source PM document.
		// We do this after serialization so the character offsets we compute
		// are in terms of the *final* markdown string (post-encoding fixup).
		const annotations = extractAnnotationRanges(pmDoc, markdown);

		return { markdown, annotations };
	};
}

/**
 * Convert a ProseMirror document node (or its JSON representation) to a
 * markdown string.
 *
 * This is the backward-compatible API. It returns a plain string.
 * Use `fromProseMirrorWithAnnotations` when you also need annotation ranges.
 */
export function fromProseMirror(schema: Schema, handlers: FromPMHandlers = defaultHandlers) {
	const withAnnotations = fromProseMirrorWithAnnotations(schema, handlers);
	return function (doc: PMNode | Record<string, unknown>): string {
		return withAnnotations(doc).markdown;
	};
}
